import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import useLang from "../hooks/useLang";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { SignOut, Brain, Gear, ShieldCheck, User, CaretDown } from "@phosphor-icons/react";

export default function Topbar() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const initials = user?.name
    ? user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <header className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center rounded-md">
            <Brain size={18} weight="duotone" />
          </div>
          <span className="font-heading text-lg font-medium tracking-tight">{t("appName")}</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher small />
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="topbar-user-menu"
                  className="flex items-center gap-2 pl-3 pr-2 h-10 border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
                >
                  <div className="w-7 h-7 bg-zinc-950 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {initials}
                  </div>
                  <span className="text-sm hidden md:inline">{user.name}</span>
                  <CaretDown size={12} className="text-zinc-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md border-zinc-200 w-56">
                <DropdownMenuLabel className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="gap-2 cursor-pointer">
                  <Gear size={15} /> {t("settings")}
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="gap-2 cursor-pointer">
                    <ShieldCheck size={15} /> {t("admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="gap-2 cursor-pointer text-red-600">
                  <SignOut size={15} /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
